import { useNavigate } from 'react-router-dom'
import { faBellExclamation } from '@fortawesome/pro-solid-svg-icons'
import { Alert } from '@/ui'
import { useHeardPlaid } from '@/components/plaid/helpers'
import type { BlockingItemRoute } from './blockingItemRoutes'

interface BlockingItemsAlertProps {
  items: BlockingItemRoute[]
}

const alertTestId = (id: string) =>
  `cubk-blocking-item-${id.toLowerCase().replace(/_/g, '-')}`

const PlaidBlockingItemAlert = ({ item }: { item: BlockingItemRoute }) => {
  const { open: openPlaidModal } = useHeardPlaid({ redirectLink: '' })
  return (
    <Alert
      variant="priority"
      customIconName={faBellExclamation}
      title={`Action required: ${item.alertTitle}`}
      description={item.description}
      dismissible
      ctaText="Start"
      ctaOnClick={() => openPlaidModal()}
      ctaProps={{ variant: 'primary' }}
      data-testid={alertTestId(item.id)}
    />
  )
}

const RouteBlockingItemAlert = ({ item }: { item: BlockingItemRoute }) => {
  const navigate = useNavigate()
  return (
    <Alert
      variant="priority"
      customIconName={faBellExclamation}
      title={`Action required: ${item.alertTitle}`}
      description={item.description}
      dismissible
      ctaText="Start"
      ctaOnClick={() => {
        if (item.route) {
          navigate(item.route, { state: { fromGettingStarted: true } })
        }
      }}
      ctaProps={{ variant: 'primary' }}
      data-testid={alertTestId(item.id)}
    />
  )
}

/**
 * Shows only the highest-priority incomplete blocking item as a single banner.
 * Once the user completes that item it drops off the list and the next one
 * surfaces. The Plaid hook is isolated to PlaidBlockingItemAlert so it only
 * mounts when a Plaid CTA is actually needed.
 */
export const BlockingItemsAlert = ({ items }: BlockingItemsAlertProps) => {
  const item = items[0]
  if (!item) return null

  return (
    <div data-testid="cubk-blocking-items-alert">
      {item.isPlaidModal ? (
        <PlaidBlockingItemAlert item={item} />
      ) : (
        <RouteBlockingItemAlert item={item} />
      )}
    </div>
  )
}
